import { Directive, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AgentProfileDto, AgentProfileFilterKey, FRENCH_REGIONS } from '@workspace/data-types';
import {
  ActionButton,
  ActionMenu,
  BaseDataTableComponent,
  DataHeaderFilter,
  DataListColumn,
} from '@workspace/ng-aura';
import { Observable } from 'rxjs';

export type AgentColumnKey = 'firstName' | 'lastName' | 'email' | 'createdAt' | 'type' | 'region';

@Directive()
export abstract class AbstractAgentsDataTableComponent<T extends AgentProfileDto>
  extends BaseDataTableComponent<T, AgentColumnKey>
  implements OnInit
{
  override topMenu = signal<ActionMenu | null>(null);
  override columns = signal<DataListColumn<AgentColumnKey>[]>([
    { key: 'firstName', label: 'Prénom', display: true },
    { key: 'lastName', label: 'Nom', display: true },
    { key: 'email', label: 'Email', display: true },
    { key: 'type', label: 'Type', display: true },
    { key: 'region', label: 'Région & Zones', display: true },
    { key: 'createdAt', label: 'Date de création', display: true },
  ]);
  override topButtons = signal<ActionButton[]>([]);
  override itemActions = signal<ActionButton[]>([
    {
      label: 'Ouvrir',
      icon: 'open_in_new',
      onClick: (agent: AgentProfileDto) => this.onItemClicked(agent as T),
      disabled: false,
      color: 'primary',
    },
  ]);
  override filters = signal<DataHeaderFilter<AgentColumnKey>[]>([
    { column: AgentProfileFilterKey.Type as AgentColumnKey, label: 'Type' },
    { column: AgentProfileFilterKey.Region as AgentColumnKey, label: 'Région' },
  ]);

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const type = params.getAll(AgentProfileFilterKey.Type);
      const region = params.getAll(AgentProfileFilterKey.Region);
      this.filterChanged({
        changed: true,
        filter: {
          ...(type.length ? { type } : {}),
          ...(region.length ? { region } : {}),
        },
      });
    });
  }

  onItemClicked(agent: T, auxClick?: boolean): void {
    const id = agent._id;
    if (!id) return;

    if (auxClick) {
      window.open(`/agents/${id}`, '_blank');
    } else {
      void this.router.navigate(['/agents', id]);
    }
  }

  abstract override getAll(): Observable<T[]>;
  abstract override countAll(): Observable<number>;

  override getDisplayForEntry(key: AgentColumnKey, rawValue: string): string {
    if (key === 'region') {
      const found = FRENCH_REGIONS.find((r) => r.code === rawValue);
      return found ? found.name : rawValue;
    }
    return rawValue;
  }
}
